import { useState } from 'react'

import { portalApi, setPortalToken, type PortalAccount } from './api'

/**
 * Вход в кабинет: почта — код — при необходимости выбор организации.
 *
 * Пароля нет нигде: восстанавливать нечего, передать коллеге нечего.
 * Выбор организации появляется, только если под этим адресом их несколько —
 * так бывает у приходящего бухгалтера, который ведёт сразу несколько фирм.
 */
export default function PortalLogin({ onEntered }: { onEntered: () => void }) {
  const [step, setStep] = useState<'email' | 'code' | 'pick'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [accounts, setAccounts] = useState<PortalAccount[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [joinOpen, setJoinOpen] = useState(false)

  async function sendCode(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await portalApi.requestCode(email.trim())
      setStep('code')
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось отправить код')
    } finally {
      setBusy(false)
    }
  }

  async function enter(portalUserId?: number) {
    setBusy(true)
    setError('')
    try {
      const res = await portalApi.verifyCode(email.trim(), code.trim(), portalUserId)
      if (res.access_token) {
        setPortalToken(res.access_token)
        onEntered()
        return
      }
      setAccounts(res.accounts)
      setStep('pick')
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось войти')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <img src="/brand/logo-full.png" alt="Бизнес Эра" className="login-logo" />
        <h1>Личный кабинет</h1>

        {step === 'email' && (
          <form onSubmit={sendCode}>
            <p className="hint">
              Введите рабочую почту — пришлём код для входа. Пароль не нужен.
            </p>
            {error && <div className="error">{error}</div>}
            <div className="field">
              <label htmlFor="pl-mail">Рабочая почта</label>
              <input
                id="pl-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ivanov@company.ru"
                autoFocus
                required
              />
            </div>
            <button className="btn primary" style={{ width: '100%' }} disabled={busy || !email.trim()}>
              {busy ? 'Отправляем…' : 'Получить код'}
            </button>
            <p className="hint" style={{ marginTop: 16, marginBottom: 0 }}>
              Доступа ещё нет?{' '}
              <button type="button" className="link-btn" onClick={() => setJoinOpen(true)}>
                Запросить
              </button>
            </p>
          </form>
        )}

        {step === 'code' && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void enter()
            }}
          >
            <p className="hint">
              Код отправлен на {email}. Если письма нет — проверьте папку «Спам».
            </p>
            {error && <div className="error">{error}</div>}
            <div className="field">
              <label htmlFor="pl-code">Код из письма</label>
              <input
                id="pl-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                autoFocus
                required
              />
            </div>
            <button className="btn primary" style={{ width: '100%' }} disabled={busy || !code.trim()}>
              {busy ? 'Проверяем…' : 'Войти'}
            </button>
            <p className="hint" style={{ marginTop: 14, marginBottom: 0 }}>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setStep('email')
                  setCode('')
                  setError('')
                }}
              >
                Другая почта
              </button>
            </p>
          </form>
        )}

        {step === 'pick' && (
          <>
            <p className="hint">
              Под этой почтой открыто несколько кабинетов. Выберите организацию.
            </p>
            {error && <div className="error">{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {accounts.map((a) => (
                <button
                  key={a.portal_user_id}
                  className="btn"
                  disabled={busy}
                  onClick={() => void enter(a.portal_user_id)}
                >
                  {a.client_name}
                  {a.is_admin && ' · администратор'}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {joinOpen && <JoinDialog onClose={() => setJoinOpen(false)} />}
    </div>
  )
}

/**
 * Запрос доступа, когда пригласить некому.
 *
 * Подтверждает администратор со стороны клиента — письмо уходит ему, не нам.
 * Ответ одинаковый в любом случае: иначе по форме можно было бы перебрать ИНН
 * и узнать, кто у нас обслуживается.
 */
function JoinDialog({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ inn: '', email: '', full_name: '', position: '', phone: '' })
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const ready = form.inn.trim().length >= 4 && form.email.trim() && form.full_name.trim()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await portalApi.join({
        inn: form.inn.trim(),
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        position: form.position.trim() || null,
        phone: form.phone.trim() || null,
      })
      setSent(true)
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось отправить запрос')
    } finally {
      setBusy(false)
    }
  }

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value })

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal" onSubmit={submit} style={{ maxWidth: 480 }}>
        <h2>Запрос доступа</h2>
        <div className="modal-body">
          {sent ? (
            <p>
              Запрос отправлен. Его подтверждает ответственный за кабинет в вашей
              организации — дождитесь письма со ссылкой для входа.
            </p>
          ) : (
            <>
              {error && <div className="error">{error}</div>}
              <p className="hint">
                Доступ подтверждает ответственный за кабинет в вашей организации.
                ИНН нужен, чтобы понять, о какой компании речь.
              </p>
              <div className="field">
                <label htmlFor="jn-inn">ИНН вашей организации</label>
                <input id="jn-inn" value={form.inn} onChange={set('inn')} required autoFocus />
              </div>
              <div className="field">
                <label htmlFor="jn-name">Фамилия и имя</label>
                <input id="jn-name" value={form.full_name} onChange={set('full_name')} required />
              </div>
              <div className="field">
                <label htmlFor="jn-mail">Рабочая почта</label>
                <input id="jn-mail" type="email" value={form.email} onChange={set('email')} required />
              </div>
              <div className="field">
                <label htmlFor="jn-pos">Должность</label>
                <input id="jn-pos" value={form.position} onChange={set('position')} />
              </div>
              <div className="field">
                <label htmlFor="jn-phone">Телефон</label>
                <input id="jn-phone" value={form.phone} onChange={set('phone')} />
              </div>
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            {sent ? 'Закрыть' : 'Отмена'}
          </button>
          {!sent && (
            <button className="btn primary" disabled={busy || !ready}>
              {busy ? 'Отправляем…' : 'Отправить'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
